import * as React from 'react'
import {Grid} from '@mui/material'
import {ToggleButtonGroup} from '@mui/material'
import {
    DateTimePicker,
    DateTimePickerProps,
    DateTimeValidationError,
    PickerChangeHandlerContext
} from '@mui/x-date-pickers'
import {makeStyles} from '@oc/styles'
import {ArrowRightAlt} from '@mui/icons-material'
import {observer} from 'mobx-react-lite'
import {SnackHandlerInstance as snackHandler} from '@oc/shell-core'
import {OcToggleButton} from './OcToggleButton'
import {messages} from '../translations'

const useStyles = makeStyles(theme => ({
    marginTop: {
        marginTop: theme.spacing(1)
    }
}))

export enum HistoryTimeRange {
    LastDay,
    LastTwoDays,
    LastWeek,
    LastHour,
    Custom
}

interface OcHistoryTimeRangePickerProps {
    className?: string
    selectedValue: HistoryTimeRange
    setSelectedValue: (value: HistoryTimeRange | null) => void
    enableCustom?: boolean
    startPickerProps?: DateTimePickerProps<Date>
    endPickerProps?: DateTimePickerProps<Date>
}

function _OcHistoryTimeRangePicker(props: OcHistoryTimeRangePickerProps) {
    const {className, selectedValue, setSelectedValue, enableCustom, startPickerProps, endPickerProps} = props
    const {classes} = useStyles()

    const setStartDatetime = React.useCallback(
        (date: Date | null, context: PickerChangeHandlerContext<DateTimeValidationError>) => {
            if (startPickerProps && endPickerProps) {
                const endValue = endPickerProps.value as number | Date
                if (!date || date <= new Date(endValue)) {
                    startPickerProps?.onChange?.(date, context)
                } else {
                    snackHandler.warning(messages.getText('messages', 'invalidStartTime'))
                }
            }
        },
        [startPickerProps, endPickerProps]
    )

    const setEndDatetime = React.useCallback(
        (date: Date | null, context: PickerChangeHandlerContext<DateTimeValidationError>) => {
            if (startPickerProps && endPickerProps) {
                const startValue = startPickerProps.value as number | Date
                if (!date || new Date(startValue) <= date) {
                    endPickerProps?.onChange?.(date, context)
                } else {
                    snackHandler.warning(messages.getText('messages', 'invalidEndTime'))
                }
            }
        },
        [startPickerProps, endPickerProps]
    )

    const onHistoryTimeRangeSelect = React.useCallback(
        (value: HistoryTimeRange) => {
            setSelectedValue(value)
        },
        [setSelectedValue]
    )

    return (
        <Grid container className={className}>
            <Grid item container justifyContent="flex-end">
                <ToggleButtonGroup value={selectedValue} color="primary">
                    <OcToggleButton
                        value={HistoryTimeRange.LastHour}
                        typoColor="textSecondary"
                        data-cy="timerange-last-hour"
                        onClick={() => onHistoryTimeRangeSelect(HistoryTimeRange.LastHour)}
                    >
                        {messages.getText('buttons', 'lastHour').toUpperCase()}
                    </OcToggleButton>
                    <OcToggleButton
                        value={HistoryTimeRange.LastDay}
                        typoColor="textSecondary"
                        data-cy="timerange-last-day"
                        onClick={() => onHistoryTimeRangeSelect(HistoryTimeRange.LastDay)}
                    >
                        {messages.getText('buttons', 'lastDay').toUpperCase()}
                    </OcToggleButton>
                    <OcToggleButton
                        value={HistoryTimeRange.LastTwoDays}
                        typoColor="textSecondary"
                        data-cy="timerange-last-two-days"
                        onClick={() => onHistoryTimeRangeSelect(HistoryTimeRange.LastTwoDays)}
                    >
                        {messages.getText('buttons', 'lastTwoDays').toUpperCase()}
                    </OcToggleButton>
                    <OcToggleButton
                        value={HistoryTimeRange.LastWeek}
                        typoColor="textSecondary"
                        data-cy="timerange-last-week"
                        onClick={() => onHistoryTimeRangeSelect(HistoryTimeRange.LastWeek)}
                    >
                        {messages.getText('buttons', 'lastWeek').toUpperCase()}
                    </OcToggleButton>
                    {enableCustom && (
                        <OcToggleButton
                            value={HistoryTimeRange.Custom}
                            typoColor="textSecondary"
                            data-cy="timerange-custom"
                            onClick={() => onHistoryTimeRangeSelect(HistoryTimeRange.Custom)}
                        >
                            {messages.getText('buttons', 'custom').toUpperCase()}
                        </OcToggleButton>
                    )}
                </ToggleButtonGroup>
            </Grid>
            {enableCustom && startPickerProps && endPickerProps && selectedValue === HistoryTimeRange.Custom && (
                <Grid item container justifyContent="flex-end" alignItems="center" className={classes.marginTop}>
                    <DateTimePicker
                        disableFuture
                        slotProps={{
                            textField: {
                                style: {
                                    marginTop: 0
                                },
                                variant: 'outlined'
                            }
                        }}
                        {...startPickerProps}
                        onChange={setStartDatetime}
                        maxDate={endPickerProps.value ?? undefined}
                    />
                    <ArrowRightAlt />
                    <DateTimePicker
                        disableFuture
                        slotProps={{
                            textField: {
                                style: {
                                    marginTop: 0
                                },
                                variant: 'outlined'
                            }
                        }}
                        {...endPickerProps}
                        onChange={setEndDatetime}
                        minDate={startPickerProps.value ?? undefined}
                    />
                </Grid>
            )}
        </Grid>
    )
}
export const OcHistoryTimeRangePicker = observer(_OcHistoryTimeRangePicker)
